import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Avatar,
    Stack,
    Divider,
    IconButton,
    Tooltip,
    alpha,
    useTheme,
    LinearProgress,
    Chip
} from '@mui/material';
import {
    Build as WrenchIcon,
    Engineering as TechnicianIcon,
    Inventory as PartsIcon,
    Timeline as AnalyticsIcon,
    FilterList as FilterIcon,
    MoreVert as MoreIcon,
    Speed as PerformanceIcon,
    HealthAndSafety as HealthIcon,
    Timer as ClockIcon
} from '@mui/icons-material';
import serviceTicketService from '../../services/serviceTicketService';
import { formatToIST } from '../../utils/dataUtils';

const ServiceMonitorPage = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [activeTickets, setActiveTickets] = useState([]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000); // 30s auto-refresh
        return () => clearInterval(interval);
    }, []);

    const fetchData = async () => {
        try {
            const [analyticsRes, ticketsRes] = await Promise.all([
                serviceTicketService.getAnalytics(),
                serviceTicketService.getAllTickets({ status: 'IN_PROGRESS' })
            ]);

            if (analyticsRes.success) setAnalytics(analyticsRes.data);
            if (ticketsRes.success) setActiveTickets(ticketsRes.data);
        } catch (error) {
            console.error('Error fetching monitor data:', error);
        } finally {
            setLoading(false);
        }
    };

    const statusMap = {
        'OPEN': { color: '#3b82f6', label: 'Queued' },
        'ASSIGNED': { color: '#6366f1', label: 'Dispatched' },
        'IN_PROGRESS': { color: '#f59e0b', label: 'Active' },
        'WAITING_SPARES': { color: '#8b5cf6', label: 'Awaiting Parts' },
        'RESOLVED': { color: '#10b981', label: 'Certified' }
    };

    if (loading && !analytics) return (
        <Box sx={{ width: '100%', p: 3 }}>
            <LinearProgress />
            <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>Initializing Service Monitor Real-time Stream...</Typography>
        </Box>
    );

    return (
        <Box p={3} className="animate-fade-in" sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header */}
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="caption" sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981', px: 1.5, py: 0.5, borderRadius: '20px', fontWeight: 800 }}>
                        ● LIVE STREAMING
                    </Typography>
                    <IconButton size="small" sx={{ bgcolor: 'white', borderRadius: '10px' }}>
                        <FilterIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>

            {/* Top KPI Cards */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: '20px', boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6' }}><ClockIcon /></Avatar>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{analytics?.avgMetrics?.avg_resolution_hours?.toFixed(1) || '0.0'}h</Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}>AVG RESOLUTION (MTTR)</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: '20px', boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981' }}><PerformanceIcon /></Avatar>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{activeTickets.length}</Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}>CONCURRENT WORK-ORDERS</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: '20px', boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }}><TechnicianIcon /></Avatar>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{analytics?.techPerformance?.length || 0}</Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}>ACTIVE FIELD ENGINEERS</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: '20px', boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6' }}><PartsIcon /></Avatar>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>88%</Typography>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}>PARTS AVAILABILITY RATE</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Active Work Flow */}
                <Grid item xs={12} lg={8}>
                    <Card sx={{ borderRadius: '24px', minHeight: '400px', boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                        <Box p={3} sx={{ borderBottom: 1, borderColor: '#f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" fontWeight={800}>Active Field Lifecycle</Typography>
                            <Typography variant="caption" color="primary.main" fontWeight={800}>AUTO-UPDATING</Typography>
                        </Box>
                        <CardContent sx={{ p: 0 }}>
                            {activeTickets.length > 0 ? (
                                <Stack divider={<Divider />}>
                                    {activeTickets.map((ticket) => (
                                        <Box key={ticket.id} p={3} sx={{ '&:hover': { bgcolor: '#f8fafc' }, transition: '0.2s' }}>
                                            <Grid container alignItems="center">
                                                <Grid item xs={1}>
                                                    <Typography variant="subtitle2" fontWeight={800} color="text.disabled">#{ticket.id}</Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Typography variant="body2" fontWeight={700}>{ticket.device_name || ticket.device_id}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{ticket.issue_type.replace('_', ' ')}</Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Avatar sx={{ width: 24, height: 24, fontSize: '10px', bgcolor: 'primary.main' }}>
                                                            {ticket.assigned_technician_name?.charAt(0)}
                                                        </Avatar>
                                                        <Typography variant="caption" fontWeight={700}>{ticket.assigned_technician_name}</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Chip
                                                        label={ticket.status}
                                                        size="small"
                                                        sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b', fontWeight: 800, fontSize: '10px' }}
                                                    />
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <ClockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                        <Typography variant="caption" fontWeight={700}>
                                                            {new Date(ticket.started_at || ticket.opened_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </Typography>
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={1} align="right">
                                                    <IconButton size="small"><MoreIcon fontSize="inherit" /></IconButton>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Box py={8} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                                    <HealthIcon sx={{ fontSize: 60, color: '#e2e8f0', mb: 2 }} />
                                    <Typography variant="body2" color="text.secondary">All field operations currently in idle or waiting state.</Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Performance Analytics */}
                <Grid item xs={12} lg={4}>
                    <Stack spacing={3}>
                        <Card sx={{ borderRadius: '24px', boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                            <Box p={3} sx={{ borderBottom: 1, borderColor: '#f1f5f9' }}>
                                <Typography variant="h6" fontWeight={800}>Technician Load</Typography>
                            </Box>
                            <CardContent>
                                <Stack spacing={3}>
                                    {analytics?.techPerformance?.map((tech, i) => (
                                        <Box key={i}>
                                            <Box display="flex" justifyContent="space-between" mb={1}>
                                                <Typography variant="caption" fontWeight={800}>{tech.name}</Typography>
                                                <Typography variant="caption" color="primary.main" fontWeight={800}>{tech.tickets_handled} tickets</Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={(tech.tickets_handled / 10) * 100}
                                                sx={{ borderRadius: '5px', height: 6, bgcolor: '#f1f5f9' }}
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: '24px', boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                            <Box p={3} sx={{ borderBottom: 1, borderColor: '#f1f5f9' }}>
                                <Typography variant="h6" fontWeight={800}>Critical Fault Domains</Typography>
                            </Box>
                            <CardContent>
                                <Stack spacing={2}>
                                    {analytics?.topIssues?.map((issue, i) => (
                                        <Box key={i} display="flex" justifyContent="space-between" alignItems="center" p={1.5} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: '12px' }}>
                                            <Typography variant="caption" fontWeight={800} color="text.secondary">{issue.issue_type.replace('_', ' ')}</Typography>
                                            <Typography variant="subtitle2" fontWeight={800} color="primary.main">{issue.count}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ServiceMonitorPage;
